import React from 'react';
import { Button, MarkdownRenderer } from '../common';
import { calculatePercentOriginal, formatPercentOriginal, isEdited } from '../../utils/textSimilarity';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const getMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/uploads/')) {
    return `${API_URL}${url}`;
  }
  return url;
};

const BlockItem = ({ block, index, onRemove, onMoveUp, onMoveDown, onEdit, isFirst, isLast }) => {
  const getBlockLabel = () => {
    switch (block.type) {
      case 'player_content':
        return block.contentType === 'prose' ? 'Player Text' :
               block.contentType === 'image' ? 'Player Image' : 'Player Audio';
      case 'gm_prose':
        return 'GM Prose';
      case 'gm_transition':
        return 'GM Transition';
      case 'gm_text':
        return 'GM Text';
      case 'gm_image':
        return 'GM Image';
      case 'gm_audio':
        return 'GM Audio';
      default:
        return 'Content';
    }
  };

  const getBlockColor = () => {
    switch (block.type) {
      case 'player_content':
        return 'border-l-blue-500 bg-blue-50';
      case 'gm_prose':
      case 'gm_transition':
      case 'gm_text':
        return 'border-l-purple-500 bg-purple-50';
      case 'gm_image':
      case 'gm_audio':
        return 'border-l-purple-400 bg-purple-50';
      default:
        return 'border-l-gray-300';
    }
  };

  // Text-based GM blocks are clickable for editing
  const isTextGMBlock = block.type === 'gm_prose' || block.type === 'gm_transition' || block.type === 'gm_text';
  // Player prose blocks are also editable
  const isPlayerProse = block.type === 'player_content' && block.contentType === 'prose';
  const isClickable = (isTextGMBlock || isPlayerProse) && onEdit;

  // Check if player content has been edited
  const hasBeenEdited = isPlayerProse && block.originalContent && isEdited(block.originalContent, block.content);
  const percentOriginal = hasBeenEdited ? calculatePercentOriginal(block.originalContent, block.content) : null;

  return (
    <div
      className={`border-l-4 p-3 rounded-r mb-2 ${getBlockColor()} ${isClickable ? 'cursor-pointer hover:brightness-95 transition-all' : ''}`}
      onClick={isClickable ? () => onEdit(block) : undefined}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-gray-500 uppercase">
              {index + 1}. {getBlockLabel()}
            </span>
            {isTextGMBlock && (
              <span className="text-xs text-gray-400">(click to edit)</span>
            )}
            {isPlayerProse && !hasBeenEdited && (
              <span className="text-xs text-gray-400">(click to edit)</span>
            )}
            {hasBeenEdited && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                {formatPercentOriginal(percentOriginal) || 'edited'}
              </span>
            )}
          </div>

          {/* Content preview */}
          {block.contentType === 'prose' || block.type === 'gm_prose' || block.type === 'gm_transition' || block.type === 'gm_text' ? (
            <div className={`text-sm text-gray-700 mt-1 line-clamp-2 ${block.type === 'gm_transition' ? 'italic' : ''}`}>
              {block.content ? (
                <MarkdownRenderer content={block.content} />
              ) : (
                <em className="text-gray-400">Empty</em>
              )}
            </div>
          ) : (block.contentType === 'image' || block.type === 'gm_image') ? (
            <div className="mt-1">
              {block.mediaUrl && (
                <img
                  src={getMediaUrl(block.mediaUrl)}
                  alt={block.content || 'GM image'}
                  className="h-20 w-auto rounded"
                />
              )}
            </div>
          ) : (block.contentType === 'audio' || block.type === 'gm_audio') ? (
            <div className="mt-1">
              {block.mediaUrl && (
                <audio controls className="h-8" onClick={(e) => e.stopPropagation()}>
                  <source src={getMediaUrl(block.mediaUrl)} />
                </audio>
              )}
            </div>
          ) : null}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
          <button
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
            disabled={isFirst}
            title="Move up"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
            onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
            disabled={isLast}
            title="Move down"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            className="p-1 text-red-400 hover:text-red-600"
            onClick={(e) => { e.stopPropagation(); onRemove(); }}
            title="Remove"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export const PassageBuilder = ({
  blocks,
  onRemoveBlock,
  onMoveBlock,
  onEditBlock,
  onExpand,
  onAddGMText,
  onAddGMImage,
  onAddGMAudio,
  uploading
}) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">Your Passage</h3>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{blocks.length} blocks</span>
          {blocks.length > 0 && onExpand && (
            <Button
              variant="link"
              size="sm"
              onClick={onExpand}
              title="Edit in expanded view"
            >
              Expand
            </Button>
          )}
        </div>
      </div>

      {blocks.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
          <p>Add content from player submissions</p>
          <p className="text-sm mt-1">or add your own GM content below</p>
        </div>
      ) : (
        <div className="max-h-[400px] overflow-y-auto pr-2">
          {blocks.map((block, index) => (
            <BlockItem
              key={block.id || index}
              block={block}
              index={index}
              onRemove={() => onRemoveBlock(index)}
              onMoveUp={() => onMoveBlock(index, index - 1)}
              onMoveDown={() => onMoveBlock(index, index + 1)}
              onEdit={onEditBlock}
              isFirst={index === 0}
              isLast={index === blocks.length - 1}
            />
          ))}
        </div>
      )}

      {/* GM content buttons */}
      <div className="flex gap-2 mt-4 flex-wrap">
        <Button
          variant="secondary"
          size="sm"
          onClick={onAddGMText}
        >
          + Add Text
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onAddGMImage}
          disabled={uploading}
          loading={uploading}
        >
          + Add Image
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={onAddGMAudio}
          disabled={uploading}
          loading={uploading}
        >
          + Add Audio
        </Button>
      </div>
    </div>
  );
};
